import { workspaceCacheOwner } from "./workspaceAssertion";
/**
 * analyticsSwr — instant-load stale-while-revalidate for the Analytics area.
 *
 * WHY THIS EXISTS. `/analytics/overview` folds a whole period of co_sessions
 * plus the equal-length comparison period; on a 90-day range that is seconds,
 * not milliseconds. The operator's ask was "instant load", and the honest way
 * to give it is not to make the query faster than it can be — it is to stop
 * showing an empty page while it runs. So the page renders the LAST payload it
 * saw for the exact same question, immediately, from sessionStorage, and
 * swaps in the fresh one when it lands. A quiet "Updating…" pill says which
 * of the two you are looking at, because a stale number that does not admit
 * it is worse than a spinner.
 *
 * THE RULES, and why each one is here:
 *
 *  • The key is the QUESTION, not the request — AND who is asking. Same
 *    endpoint + same params in any order = same key, so re-selecting a scope
 *    you already looked at is free. Empty/absent params are dropped so
 *    `funnel_id=""` and "no funnel_id" cannot cache the same answer under two
 *    keys. The owner (the authenticated user) is part of the key because
 *    sessionStorage outlives a logout in the same tab: without it, user B
 *    logging in after user A would paint A's workspace revenue under B's
 *    "Last 30 days", wearing nothing but an "Updating…" pill. AuthContext sets
 *    the owner and clears the namespace on every login/logout — the key prefix
 *    is the belt to that pair of braces.
 *  • 24h TTL. A cached overview is a snapshot of a period that mostly cannot
 *    change (closed reporting-zone days), but "mostly" is not "never" — refunds land
 *    late. A day is the longest we are willing to show a figure before we
 *    refuse to show it at all; past that we render the skeleton instead.
 *  • ~20 entries, oldest evicted first. sessionStorage is a few MB shared with
 *    the whole app; an unbounded analytics cache would eventually throw
 *    QuotaExceededError on some OTHER feature's write, which is a spectacular
 *    way to break a page that has nothing to do with analytics.
 *  • EVERY storage call is wrapped. Safari private mode throws on setItem,
 *    an iframe with third-party cookies blocked throws on read, and a
 *    half-written entry parses as garbage. None of those may take the page
 *    down — the cache is an optimisation, and an optimisation that can crash
 *    the screen is a bug, not an optimisation.
 *
 * The cache is intentionally *not* invalidated by writes elsewhere in the app:
 * nothing in the CRM writes to the numbers this page reads, and the
 * revalidate-on-mount pass covers the money that lands while you are away.
 */
import {
  useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore,
} from "react";
import api from "./api";

export const CACHE_PREFIX = "lb.an.swr.";
/** 24 hours. Older than this and we would rather show nothing than a lie. */
export const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
/** Hard cap on entries we keep, oldest-first eviction. */
export const CACHE_MAX = 20;

/* ── storage plumbing ────────────────────────────────────────────────────── */

/** sessionStorage when it exists AND works, otherwise null (never throws). */
export function defaultStorage() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch {
    // Blocked by the embedder (third-party context, hardened privacy mode).
    return null;
  }
}

function pickStorage(opts) {
  // `storage: null` is a deliberate "no cache", so only an ABSENT key falls
  // back to the default. `opts.storage === null` must not silently re-enable it.
  return opts && "storage" in opts ? opts.storage : defaultStorage();
}

function safeGet(st, key) {
  try {
    return st.getItem(key);
  } catch {
    return null;
  }
}

function safeRemove(st, key) {
  try {
    st.removeItem(key);
  } catch {
    /* nothing we can do, and nothing that should surface */
  }
}

/**
 * Every key WE own, in this storage.
 *
 * Uses the Storage index API when present (real sessionStorage) and falls back
 * to own-enumerable keys so a plain-object fake works in tests without
 * re-implementing the DOM.
 */
export function namespacedKeys(st) {
  const out = [];
  try {
    if (st && typeof st.key === "function" && typeof st.length === "number") {
      for (let i = 0; i < st.length; i += 1) {
        const k = st.key(i);
        if (typeof k === "string" && k.startsWith(CACHE_PREFIX)) out.push(k);
      }
      return out;
    }
    for (const k of Object.keys(st || {})) {
      if (k.startsWith(CACHE_PREFIX)) out.push(k);
    }
  } catch {
    return out;
  }
  return out;
}

/* ── partitions inside the shared namespace ──────────────────────────────── */

/**
 * Key substrings that mark entries in this namespace as belonging to ANOTHER
 * module (today: `lib/metaInstantCache`, which shares the prefix on purpose so
 * it inherits the login/logout wipe).
 *
 * WHY THIS EXISTS. Sharing the namespace also meant sharing the eviction, and
 * the eviction here is written for THIS module's payloads: a 20-entry cap that
 * is right for small overview bodies, and a quota fallback that clears the
 * whole prefix. Applied to a neighbour holding 270 KB campaign snapshots, both
 * were wrong — the neighbour's entries counted against a cap they are not
 * sized for, and any quota error in an unrelated feature's write deleted every
 * one of them with no UI signal at all. So pruning is now partition-aware.
 *
 * `clearCache` is deliberately NOT: logout must take everything, and a
 * partition that survived a logout would be one operator's money in the next
 * operator's tab.
 */
const FOREIGN_KEY_MARKS = new Set();

/** Claim a partition of this namespace. Returns the number of marks known. */
export function registerForeignKeyMark(mark) {
  const m = typeof mark === "string" ? mark : "";
  if (m) FOREIGN_KEY_MARKS.add(m);
  return FOREIGN_KEY_MARKS.size;
}

/** True when `key` belongs to a registered neighbour rather than to us. */
export function isForeignKey(key) {
  if (!FOREIGN_KEY_MARKS.size) return false;
  const k = String(key || "");
  for (const m of FOREIGN_KEY_MARKS) {
    if (k.includes(m)) return true;
  }
  return false;
}

/* ── who is asking ───────────────────────────────────────────────────────── */

/**
 * The identity every cache key is stamped with.
 *
 * Module-level rather than threaded through each call site ON PURPOSE: a hook
 * argument only has to be forgotten once for the tab to start serving one
 * operator's money to the next one, and there is nothing in a payload that
 * would give the mistake away. AuthContext owns the value.
 */
let cacheOwner = "";
const ownerListeners = new Set();

export function getCacheOwner() {
  return workspaceCacheOwner(cacheOwner, typeof window === "undefined" ? "" : window.location.pathname);
}

/** Set the owner. Returns true when it actually changed. Never throws. */
export function setCacheOwner(id) {
  const next = id === null || id === undefined ? "" : String(id);
  if (next === cacheOwner) return false;
  cacheOwner = next;
  ownerListeners.forEach((fn) => {
    try { fn(next); } catch { /* a listener must not break the swap */ }
  });
  return true;
}

/** Subscribe to owner changes (useSyncExternalStore contract). */
export function subscribeCacheOwner(fn) {
  ownerListeners.add(fn);
  return () => { ownerListeners.delete(fn); };
}

/* ── the key ─────────────────────────────────────────────────────────────── */

/**
 * A stable cache key for (owner, endpoint, params).
 *
 * Sorted so param order never forks the cache; empty values dropped so the
 * "all funnels" request has ONE key whether the caller passed `funnel_id: ""`
 * or left it out entirely. Arrays join on "," — the wire format for funnel_ids.
 * `owner` defaults to the module owner; pass it explicitly in tests.
 */
export function cacheKey(endpoint, params, owner) {
  const who = owner === undefined ? getCacheOwner() : (owner || "");
  const p = params && typeof params === "object" ? params : {};
  const parts = [];
  for (const k of Object.keys(p).sort()) {
    const v = p[k];
    if (v === null || v === undefined || v === "") continue;
    if (Array.isArray(v)) {
      if (!v.length) continue;
      parts.push(`${k}=${v.join(",")}`);
      continue;
    }
    if (typeof v === "boolean") {
      parts.push(`${k}=${v ? "1" : "0"}`);
      continue;
    }
    parts.push(`${k}=${String(v)}`);
  }
  return `${CACHE_PREFIX}${who}|${String(endpoint || "")}?${parts.join("&")}`;
}

/* ── read / write / evict ────────────────────────────────────────────────── */

/**
 * The cached payload for `key`, or null.
 *
 * Returns `{ payload, at, age }` so the caller can date what it is showing.
 * Anything unreadable, un-parseable, shaped wrong, expired, or stamped in the
 * FUTURE (clock moved backwards — we cannot reason about its age) is dropped
 * from storage and reported as a miss.
 */
export function readCache(key, opts = {}) {
  const st = pickStorage(opts);
  if (!st || !key) return null;
  const raw = safeGet(st, key);
  if (!raw) return null;

  let rec;
  try {
    rec = JSON.parse(raw);
  } catch {
    safeRemove(st, key);
    return null;
  }
  if (!rec || typeof rec !== "object" || typeof rec.at !== "number"
      || !Number.isFinite(rec.at) || !("payload" in rec)) {
    safeRemove(st, key);
    return null;
  }

  const now = typeof opts.now === "number" ? opts.now : Date.now();
  const ttl = typeof opts.ttl === "number" ? opts.ttl : CACHE_TTL_MS;
  const age = now - rec.at;
  if (age < 0 || age > ttl) {
    safeRemove(st, key);
    return null;
  }
  return { payload: rec.payload, at: rec.at, age };
}

/**
 * Drop entries until at most `max` of ours remain, oldest `at` first.
 *
 * "Ours" excludes registered neighbour partitions (see `isForeignKey`) — this
 * cap is an entry COUNT sized for this module's payloads, and applying it to a
 * neighbour storing far larger bodies evicts their data to make room for ours
 * against a limit that was never about them. Pass `ownOnly: false` to sweep
 * the whole namespace.
 *
 * An entry we cannot parse counts as infinitely old and goes first — it is
 * dead weight that would otherwise survive every eviction pass forever.
 * Returns the keys removed (handy in tests and for nothing else).
 */
export function pruneCache(opts = {}) {
  const st = pickStorage(opts);
  if (!st) return [];
  const max = typeof opts.max === "number" ? opts.max : CACHE_MAX;
  const ownOnly = opts.ownOnly !== false;
  const keys = namespacedKeys(st).filter((k) => !(ownOnly && isForeignKey(k)));
  const rows = keys.map((k) => {
    let at = -Infinity;
    try {
      const rec = JSON.parse(safeGet(st, k) || "null");
      if (rec && typeof rec.at === "number" && Number.isFinite(rec.at)) at = rec.at;
    } catch {
      at = -Infinity;
    }
    return { key: k, at };
  });
  if (rows.length <= max) return [];
  rows.sort((a, b) => a.at - b.at); // oldest first
  const doomed = rows.slice(0, rows.length - max);
  doomed.forEach((r) => safeRemove(st, r.key));
  return doomed.map((r) => r.key);
}

/**
 * Store `payload` under `key`, stamped now.
 *
 * Prunes to CACHE_MAX-1 first so the write itself lands inside the cap. If the
 * write still throws (quota, private mode, a payload with a cycle) we give up
 * OUR OWN partition first and retry; only if that still fails do we clear the
 * whole namespace, neighbours included, and retry a last time. A second
 * failure is swallowed. Returns true only if the value is actually in storage.
 *
 * The own-partition step is the fix for a real asymmetry: the neighbour that
 * shares this prefix (`metaInstantCache`) holds ~270 KB payloads and had every
 * one of them deleted by whatever unrelated feature happened to hit the quota
 * first — the exact pressure its own byte budget exists to avoid. Our entries
 * are small and cheap to refetch, so they go first.
 */
export function writeCache(key, payload, opts = {}) {
  const st = pickStorage(opts);
  if (!st || !key) return false;
  const now = typeof opts.now === "number" ? opts.now : Date.now();
  const max = typeof opts.max === "number" ? opts.max : CACHE_MAX;

  let body;
  try {
    body = JSON.stringify({ at: now, payload });
  } catch {
    return false; // not serialisable — nothing to cache, nothing to report
  }

  // Reserve a slot for ourselves when this key is new.
  const already = namespacedKeys(st).includes(key);
  pruneCache({ ...opts, storage: st, max: already ? max : Math.max(0, max - 1) });

  try {
    st.setItem(key, body);
    return true;
  } catch {
    // 1. Our own entries, all of them. Cheap to refetch, and the pressure may
    //    well be ours.
    pruneCache({ storage: st, max: 0 });
    try {
      st.setItem(key, body);
      return true;
    } catch { /* still no room — the pressure is elsewhere */ }
    // 2. Last resort: the whole namespace, neighbours included.
    clearCache({ storage: st });
    try {
      st.setItem(key, body);
      return true;
    } catch {
      return false;
    }
  }
}

/** Remove every entry this module owns. Leaves the rest of storage alone. */
export function clearCache(opts = {}) {
  const st = pickStorage(opts);
  if (!st) return 0;
  const keys = namespacedKeys(st);
  keys.forEach((k) => safeRemove(st, k));
  return keys.length;
}

/* ── the hook ────────────────────────────────────────────────────────────── */

/**
 * GET `endpoint` with `params`, stale-while-revalidate.
 *
 * Returns:
 *   data         the freshest payload we have (cached OR fetched), or null
 *   stale        true ONLY while a revalidate is genuinely in flight over
 *                cached data — it is cleared whether the request lands or
 *                fails, because a spinner that never stops reads as "still
 *                refreshing" over numbers that may be a day old
 *   loading      true only when there is nothing at all to render yet
 *   error        the failed refetch, IF we have no data to fall back on
 *   staleError   the failed refetch when we DO have data behind it — the
 *                caller owes the operator a quiet "couldn't refresh" chip;
 *                silence here is how yesterday's money passes for today's
 *   cachedAt     epoch ms of the cached payload currently on screen (or null)
 *   refresh()    force a revalidate, bypassing nothing but the render gate
 *
 * The last-good payload is never thrown away on a failed refetch: a network
 * blip must not blank a page full of numbers that were correct 30 seconds ago.
 * Responses are sequence-gated, so a slow request for last month can never
 * overwrite the fast one for today that the operator asked for after it.
 */
export function useSwrGet(endpoint, params, options = {}) {
  const enabled = options.enabled !== false;
  // Params are rebuilt every render by callers; the STRING is the identity.
  const paramSig = JSON.stringify(params || {});
  // Subscribed, not merely read: a login/logout swaps the owner under a mounted
  // page, and the key has to move with it or the memo keeps the old identity.
  const owner = useSyncExternalStore(subscribeCacheOwner, getCacheOwner, getCacheOwner);
  const key = useMemo(() => cacheKey(endpoint, params, owner),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, paramSig, owner]);

  const [state, setState] = useState(() => {
    const hit = enabled ? readCache(key) : null;
    return {
      key,
      data: hit ? hit.payload : null,
      cachedAt: hit ? hit.at : null,
      stale: !!hit,
      loading: enabled && !hit,
      error: null,
      staleError: null,
    };
  });

  const seqRef = useRef(0);
  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    let alive = true;

    // Show the cached answer for THIS key the instant the key changes, so
    // switching scope back to one you already viewed is a paint, not a wait.
    const hit = readCache(key);
    setState((prev) => ({
      key,
      data: hit ? hit.payload : (prev.key === key ? prev.data : null),
      cachedAt: hit ? hit.at : null,
      stale: !!hit,
      loading: !hit && !(prev.key === key && prev.data),
      error: null,
      staleError: null,
    }));

    (async () => {
      try {
        const res = await api.get(endpoint, { params: params || {} });
        if (!alive || seqRef.current !== seq) return;
        const payload = res?.data;
        writeCache(key, payload);
        setState({
          key, data: payload, cachedAt: null,
          stale: false, loading: false, error: null, staleError: null,
        });
      } catch (err) {
        if (!alive || seqRef.current !== seq) return;
        // Keep whatever is on screen — but STOP claiming a refresh is running.
        // `stale` was set at the top of this effect; leaving it true parks an
        // "Updating…" spinner over cached figures forever, and the page's error
        // banner is gated on having no data, so the operator would get no
        // signal at all that they are reading yesterday.
        const failure = err || new Error("request failed");
        setState((prev) => ({
          ...prev,
          key,
          loading: false,
          stale: false,
          error: prev.data ? null : failure,
          staleError: prev.data ? failure : null,
        }));
      }
    })();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, key, paramSig, enabled, nonce]);

  return {
    data: state.data,
    stale: state.stale,
    loading: state.loading,
    error: state.error,
    staleError: state.staleError,
    cachedAt: state.cachedAt,
    refresh,
  };
}

export default useSwrGet;
